import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import pickBy from 'lodash/pickBy';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMount } from 'react-use';

import { Logger } from '../../../../common/infrastructure/logger';
import {
  CustomError,
  useAppErrors,
} from '../../../../common/contexts/app-error-context';
import Responsive, {
  useMobile,
} from '../../../../common/components/responsive/Responsive';
import {
  compilePath,
  FEEDBACK_WIZARD_STEP,
  namedRoute,
} from '../../../../common/utils/helpers/routes';
import {
  initialLoadingPhotosHandler,
  newPhotosHandler,
  photosReducer,
} from '../../../../common/components/controls/photo-upload/PhotoUpload';
import {
  selectFormState,
  formStateChanged,
  clearFormState,
  selectCurrentFormId,
  currentFormIdChanged,
  selectRoutablePoints,
} from '../../../../common/store/item-edit';
import { getLastSubmittedEmail } from '../../../../common/utils/helpers/emailCache';
import {
  EDIT_SUGGESTION_TYPES,
  EDIT_SUGGESTION_VERSION,
} from '../../../../common/utils/constants/constants';
import { selectUserNotificationEnabled } from '../../../../common/store/user-notificaition-settings';
import { selectSearchParams } from '../../../../common/store/search';
import { getItemMetricParams } from '../../../../common/utils/helpers/item';
import {
  enhanceEditSuggestoinPayload,
  getReferrer,
} from '../../../../common/utils/helpers/editSuggestions';
import { appearances } from '../../../../common/components/controls/button/Button';
import Form, { FormField } from '../../../../common/components/forms/form/Form';
import { FIELD_TYPES } from '../../../../common/components/forms/field/Field';
import { validators } from '../../../../common/utils/helpers/validators';
import {
  sendEditSuggestionAction,
  storeLastSubmittedEmailAction,
  analyticsTrackAction,
} from '../../../../common/utils/helpers/EditSuggestionActions';
import DefaultFormFooterHint from '../../../../common/components/forms/default-form-footer-hint/DefaultFormFooterHint';
import AddRoutablePointsForm, {
  formValidator as routablePointsFormValidator,
} from '../../../../common/components/forms/add-routable-points-form/AddRoutablePointsForm';

import './EditAddressForm.scss';

const addressValidator = validators.required('Address');

const getAdditionalInformationFormFields = ({
  attachmentsDispatch,
}): FormField[] => [
  {
    field: 'email',
    label: 'Your email',
    defaultValue: '',
    getPrefillValue: () => getLastSubmittedEmail(),
    required: true,
    validate: validators.compose(
      validators.required('Email'),
      validators.email,
    ),
    type: FIELD_TYPES.input,
    controlProps: {
      placeholder: 'You will get an email when changes are made',
      // name and autocomplete are required for email autocompletion done by browser
      name: 'email',
      autoComplete: 'email',
    },
  },
  {
    field: 'comment',
    label: 'Comments',
    defaultValue: '',
    type: FIELD_TYPES.textArea,
    controlProps: {
      placeholder: 'Add here some additional information',
    },
  },
  {
    field: 'attachments',
    label: 'Photos',
    defaultValue: {},
    type: FIELD_TYPES.photos,
    controlProps: { photosDispatch: attachmentsDispatch },
  },
];

const getFeedbackFormFields = ({
  addressHint,
  onOpenRoutablePoints,
}: { addressHint?; onOpenRoutablePoints? } = {}): FormField[] => [
  {
    field: 'address',
    label: 'Address',
    defaultValue: {
      value: '',
      readonly: false,
    },
    type: FIELD_TYPES.textArea,
    required: true,
    getValueFromData: (item) => ({
      value: item.properties.place_name,
      readonly: true,
    }),
    getFormControlValue: (data) => data?.value,
    beforeChange: (value) => ({ value, readonly: false }),
    validate: (data) => addressValidator(data?.value),
    checkChanged: (value, initialValue, outerInput) => {
      return (
        (value.readonly ? false : !isEqual(value, initialValue)) ||
        (value.value && outerInput?.itemMarkerDragged)
      );
    },
    hint: addressHint,
    controlProps: {
      placeholder: 'Enter the address of business',
    },
  },
  {
    field: 'routable-points-button',
    type: 'button',
    controlProps: {
      children: 'Set Pickup/Arrival',
      appearance: appearances.PRIMARY,
      onClick: onOpenRoutablePoints,
      type: 'button',
    },
  },
  {
    field: 'routable-points',
    type: 'form',
    // should be null for correct initialization of a form
    defaultValue: null,
    validate: validators.validateSelected(
      routablePointsFormValidator,
      (value) => value?.values,
    ),
  },
];

const getJointFormFields = ({
  attachmentsDispatch,
  onOpenRoutablePoints,
}): FormField[] => [
  ...getFeedbackFormFields({
    addressHint: 'Move the location pin to edit the address',
    onOpenRoutablePoints,
  }),
  ...getAdditionalInformationFormFields({
    attachmentsDispatch,
  }),
];

const getAddressEditSuggestionPayload = ({
  values,
  mapRoutablePoints,
  itemDragPosition,
  itemMarkerDragged,
  item,
  userLocation,
  searchQuery,
  searchSelectedFeatureIndex,
  notificationsEnabled,
}: {
  values: Record<string, unknown> & {
    attachments: { fileId }[];
    address: {
      readonly?: boolean;
      value?;
    };
    'routable-points';
  };
  mapRoutablePoints;
  itemDragPosition;
  itemMarkerDragged;
  item;
  userLocation;
  searchQuery;
  searchSelectedFeatureIndex;
  notificationsEnabled;
}) => {
  const routablePoints =
    values?.['routable-points']?.values?.['routablePoints'];

  const payload = pickBy(
    {
      version: EDIT_SUGGESTION_VERSION,
      action: 'update',
      reason: 'incorrect_data',
      location: {
        longitude: item.geometry.coordinates[0],
        latitude: item.geometry.coordinates[1],
      },
      userEmail: values.email,
      userLocation: !isEmpty(userLocation)
        ? {
            longitude: userLocation.longitude,
            latitude: userLocation.latitude,
          }
        : null,
      referrer: getReferrer(),
      notificationsDisabled: !notificationsEnabled,
      comment: values.comment,
      attachments: Object.values(values.attachments)
        .filter(({ fileId }) => fileId)
        .map(({ fileId }) => fileId),
      searchQuery: searchQuery,
      selectedFeature: JSON.stringify(item),
      selectedFeatureIndex: searchSelectedFeatureIndex,
      placeName: item.properties.place_name,
      mapboxId: item.properties.mapbox_id,
      changes: pickBy(
        {
          address: values.address?.readonly ? null : values.address?.value,
          location: itemMarkerDragged
            ? {
                longitude: itemDragPosition[0],
                latitude: itemDragPosition[1],
              }
            : null,
          routablePoints: routablePoints?.map((routablePoint, idx) =>
            pickBy(
              {
                ...routablePoint,
                location: {
                  longitude: mapRoutablePoints[idx].location[0],
                  latitude: mapRoutablePoints[idx].location[1],
                },
              },
              Boolean,
            ),
          ),
        },
        (val) => !isNil(val) && !isEmpty(val),
      ),
    },
    (val) => !isNil(val),
  );

  enhanceEditSuggestoinPayload(payload, item);

  return payload;
};

const FORM_ID = 'EditAddressForm';

export interface EditAddressFormProps {
  item;
  itemDragPosition;
  itemMarkerDragged;
  searchQuery;
  searchSelectedFeatureIndex;
  userLocation;
  onSubmit;
  onCancel;
}

interface RouteMatch {
  wizardStep;
}

const EditAddressForm = ({
  item,
  itemDragPosition,
  itemMarkerDragged,
  searchQuery,
  searchSelectedFeatureIndex,
  userLocation,
  onSubmit,
  onCancel,
}: EditAddressFormProps) => {
  const { showErrorPage } = useAppErrors();
  const history = useHistory();
  const match = useRouteMatch<RouteMatch>();
  const isMobile = useMobile();
  const [isLoading, setLoadingState] = useState(false);
  const searchParams = useSelector(selectSearchParams);
  const analyticsTrackParams = getItemMetricParams(item, {
    query: searchParams.query,
  });

  const notificationsEnabled = useSelector(selectUserNotificationEnabled);
  const formState = useSelector(selectFormState);
  const currentFormId = useSelector(selectCurrentFormId);
  const formStateRef = useRef<Record<string, any>>();
  formStateRef.current = formState;

  const dispatch = useDispatch();
  const setFormState = useCallback(
    (formState) => dispatch(formStateChanged(formState)),
    [dispatch],
  );

  const [isFeedbackFormShown, setIsFeedbackFormShown] = useState(!!formState);

  // guarantee that feedback is always filled with all the data on mobile
  useEffect(() => {
    if (!isFeedbackFormShown) {
      if (
        isMobile &&
        match.params.wizardStep !== FEEDBACK_WIZARD_STEP.feedback
      ) {
        history.replace({
          pathname: compilePath('edit', null, {
            wizardStep: FEEDBACK_WIZARD_STEP.feedback,
          }),
          search: history.location.search,
        });
      } else {
        setIsFeedbackFormShown(true);
      }
    } else {
      if (isMobile && !match.params.wizardStep) {
        history.replace({
          pathname: compilePath('edit', null, {
            wizardStep: FEEDBACK_WIZARD_STEP.feedback,
          }),
          search: history.location.search,
        });
      }
    }
  }, [isFeedbackFormShown, isMobile, match, history]);

  const attachmentsDispatch = useCallback(
    (action) => {
      if (!formStateRef.current) {
        return;
      }

      const newAttachments = photosReducer(
        (formStateRef.current.values as Record<string, any>).attachments,
        action,
      );
      const newFormState = {
        ...formStateRef.current,
        values: {
          ...formStateRef.current.values,
          attachments: newAttachments,
        },
      };
      // set ref to handle sequential actions between state updates and rerenders
      formStateRef.current = newFormState;

      setFormState(newFormState);

      return action;
    },
    [setFormState],
  );

  const attachments = useMemo(() => formState?.values.attachments, [formState]);

  // reload all still loading images, because it won't be handled anyway
  useMount(() => {
    // clean up other forms to keep local storage size as small as possible
    if (currentFormId !== FORM_ID) {
      dispatch(clearFormState());
      dispatch(currentFormIdChanged(FORM_ID));
    }

    if (attachments) {
      initialLoadingPhotosHandler(attachments, attachmentsDispatch);
    }
  });

  useEffect(() => {
    if (attachments) {
      newPhotosHandler(attachments, attachmentsDispatch);
    }
  }, [attachments, attachmentsDispatch]);

  const handleOpenRoutablePoints = useCallback(
    () =>
      history.push({
        pathname: compilePath('edit', null, {
          wizardStep: FEEDBACK_WIZARD_STEP.routablePoints,
        }),
        search: history.location.search,
      }),
    [history],
  );

  const jointFormFields = useMemo(
    () =>
      getJointFormFields({
        attachmentsDispatch,
        onOpenRoutablePoints: handleOpenRoutablePoints,
      }),
    [attachmentsDispatch, handleOpenRoutablePoints],
  );
  const feedbackFormFields = useMemo(
    () =>
      getFeedbackFormFields({
        onOpenRoutablePoints: handleOpenRoutablePoints,
      }),
    [handleOpenRoutablePoints],
  );
  const additionalInformationFormFields = useMemo(
    () =>
      getAdditionalInformationFormFields({
        attachmentsDispatch,
      }),
    [attachmentsDispatch],
  );

  const formInitialValues = useMemo(
    () =>
      jointFormFields.reduce(
        (acc, { field, defaultValue, getValueFromData }) => {
          acc[field] =
            (getValueFromData && getValueFromData(item)) || defaultValue;
          return acc;
        },
        {},
      ),
    [item, jointFormFields],
  );

  useEffect(() => {
    if (!formState) {
      const values = jointFormFields.reduce(
        (acc, { field, defaultValue, getPrefillValue, getValueFromData }) => {
          acc[field] =
            (getValueFromData && getValueFromData(item)) ||
            (getPrefillValue && getPrefillValue()) ||
            defaultValue;
          return acc;
        },
        {},
      );

      setFormState({ values });
    }
  }, [formState, setFormState, item, jointFormFields]);

  const mapRoutablePoints = useSelector(selectRoutablePoints);

  const handleSubmit = useCallback(
    async (values) => {
      const payload = getAddressEditSuggestionPayload({
        values,
        item,
        itemDragPosition,
        itemMarkerDragged,
        searchQuery,
        searchSelectedFeatureIndex,
        userLocation,
        notificationsEnabled,
        mapRoutablePoints,
      });

      try {
        setLoadingState(true);

        const editSuggestionResult = await sendEditSuggestionAction(
          {
            editSuggestionType: EDIT_SUGGESTION_TYPES.address,
            origin: FORM_ID,
          },
          payload,
        );

        storeLastSubmittedEmailAction(
          {
            email: values.email,
            origin: FORM_ID,
          },
          editSuggestionResult,
        );

        analyticsTrackAction({
          analyticsTrackingMessage: 'Address Edit Submit',
          origin: FORM_ID,
          analyticsTrackParams,
        });

        if (onSubmit) {
          onSubmit(editSuggestionResult.result);
        }
      } catch (error) {
        const _error: CustomError = error as CustomError;

        Logger.error('Error while submitting edit suggestion', _error.message);

        showErrorPage({
          type: _error.type,
          message: _error.message,
        } as CustomError);
      } finally {
        setLoadingState(false);
      }
    },
    [
      item,
      itemDragPosition,
      itemMarkerDragged,
      searchQuery,
      searchSelectedFeatureIndex,
      userLocation,
      notificationsEnabled,
      analyticsTrackParams,
      onSubmit,
      showErrorPage,
      mapRoutablePoints,
    ],
  );

  const handleFeedbackFormSubmit = useCallback(
    () =>
      history.push({
        pathname: compilePath('edit', null, {
          wizardStep: FEEDBACK_WIZARD_STEP.additionalInformation,
        }),
        search: history.location.search,
      }),
    [history],
  );

  const handleRoutablePointsFormSubmit = useCallback(
    () =>
      history.push({
        pathname: compilePath('edit'),
        search: history.location.search,
      }),
    [history],
  );

  const handleRoutablePointsFormStateChange = useCallback(
    (newRoutablePointsFormState) => {
      if (!formStateRef.current) {
        return;
      }

      const newFormState = {
        ...formStateRef.current,
        values: {
          ...formStateRef.current.values,
          'routable-points': newRoutablePointsFormState,
        },
      };
      // set ref to handle sequential actions between state updates and rerenders
      formStateRef.current = newFormState;

      setFormState(newFormState);
    },
    [setFormState],
  );

  const validateForm = useCallback(
    (values) => {
      if (mapRoutablePoints.some((routablePoint) => !routablePoint.location)) {
        return 'One or several routable points have uninitialized locations.';
      }

      // check that marker has been moved, otherwise there should be changes to the fields
      if (itemMarkerDragged) {
        return false;
      }

      const nonValuableFieldsToOmit = ['email', 'comment', 'attachments'];
      const currentValues = omit(values, nonValuableFieldsToOmit);
      const initialValues = omit(formInitialValues, nonValuableFieldsToOmit);

      if (!currentValues['routable-points']?.values?.routablePoints?.length) {
        // for comparison purposes only, rewrite when there is editing of routable points
        currentValues['routable-points'] = null;
      }

      return isEqual(currentValues, initialValues)
        ? 'Changes to other fields are required to submit the feedback'
        : null;
    },
    [formInitialValues, itemMarkerDragged, mapRoutablePoints],
  );

  const { wizardStep } = match.params;

  const outerInput = useMemo(
    () => ({ itemDragPosition, itemMarkerDragged }),
    [itemDragPosition, itemMarkerDragged],
  );

  return (
    <Switch>
      <Route
        path={namedRoute(
          'routable-points',
          compilePath('edit', null, {
            wizardStep: FEEDBACK_WIZARD_STEP.routablePoints,
          }),
        )}
      >
        <AddRoutablePointsForm
          baseUrl={compilePath('edit', null, {
            wizardStep: FEEDBACK_WIZARD_STEP.routablePoints,
          })}
          item={item}
          formState={formState?.values['routable-points']}
          onFormStateChange={handleRoutablePointsFormStateChange}
          onSubmit={handleRoutablePointsFormSubmit}
        />
      </Route>
      <Route>
        <Responsive mobile={false}>
          <Form
            state={formState}
            fields={jointFormFields}
            initialValues={formInitialValues}
            outerInput={outerInput}
            formDisabled={isLoading}
            validate={validateForm}
            onSubmit={handleSubmit}
            onCancel={onCancel}
            onStateChange={setFormState}
            footerHint={<DefaultFormFooterHint />}
          />
        </Responsive>
        <Responsive mobile={true}>
          {wizardStep === FEEDBACK_WIZARD_STEP.feedback && (
            <Form
              state={formState}
              fields={feedbackFormFields}
              initialValues={formInitialValues}
              validate={validateForm}
              outerInput={outerInput}
              submitLabel="Next"
              stepLabel="1/2"
              onSubmit={handleFeedbackFormSubmit}
              onStateChange={setFormState}
            />
          )}

          {wizardStep === FEEDBACK_WIZARD_STEP.additionalInformation && (
            <Form
              state={formState}
              fields={additionalInformationFormFields}
              initialValues={formInitialValues}
              validate={validateForm}
              formDisabled={isLoading}
              stepLabel="2/2"
              onSubmit={handleSubmit}
              onStateChange={setFormState}
              footerHint={<DefaultFormFooterHint />}
            />
          )}
        </Responsive>
      </Route>
    </Switch>
  );
};

export default EditAddressForm;
